import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Post } from '../lib/types';
import { slugifyTag } from '../lib/tagSlug';

type ArchiveTableProps = {
  posts: Post[];
  initialQuery?: string;
};

const PAGE_SIZE = 12;
type ViewMode = 'card' | 'table';

function formatFullDate(dateString?: string | null) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function ArchiveTable({ posts, initialQuery = '' }: ArchiveTableProps) {
  const [query, setQuery] = useState(initialQuery);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('card');

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  const normalizedQuery = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!normalizedQuery) return posts;

    return posts.filter((post) => {
      const target = [
        post.title,
        post.author?.name,
        post.category,
        ...(post.tags || [])
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return target.includes(normalizedQuery);
    });
  }, [posts, normalizedQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    setPage(0);
  }, [normalizedQuery]);

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages - 1));
  }, [totalPages]);

  const pageStart = page * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const pagedPosts = filtered.slice(pageStart, pageEnd);

  const shownStart = filtered.length === 0 ? 0 : pageStart + 1;
  const shownEnd = Math.min(pageEnd, filtered.length);

  return (
    <section className="archive-panel">
      <div className="archive-controls">
        <label className="archive-search">
          <span className="sr-only">Search archive</span>
          <input
            type="search"
            placeholder="Search by title, author, category, or tag"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="archive-controls-right">
          <div className="archive-results-meta">
            {filtered.length > 0 ? (
              <span>
                Showing {shownStart}-{shownEnd} of {filtered.length} posts
              </span>
            ) : (
              <span>No posts found</span>
            )}
          </div>
          <div className="archive-view-toggle" role="group" aria-label="Select archive layout">
            <button
              type="button"
              aria-pressed={viewMode === 'card'}
              className={viewMode === 'card' ? 'active' : ''}
              onClick={() => setViewMode('card')}
            >
              Cards
            </button>
            <button
              type="button"
              aria-pressed={viewMode === 'table'}
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'card' ? (
        <div className="archive-card-grid card-grid">
          {pagedPosts.length === 0 ? (
            <div className="archive-card-empty">
              {posts.length === 0
                ? 'Archive is empty—publish your first story to fill it.'
                : 'No posts match this search yet.'}
            </div>
          ) : (
            pagedPosts.map((post) => (
              <article className="card archive-card" key={post.slug}>
                <Link
                  to={`/posts/${post.slug}`}
                  className="card-media"
                  aria-label={`Read ${post.title}`}
                >
                  <img
                    src={
                      post.header_image_url ||
                      'https://images.unsplash.com/photo-1505685296765-3a2736de412f?auto=format&fit=crop&w=900&q=80'
                    }
                    alt={post.title}
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  />
                </Link>
                <div className="card-body">
                  <div className="archive-card-topline">
                    <div className="tag-pill">{post.category || 'General'}</div>
                    <span className="archive-card-date">
                      {formatFullDate(post.date_published || post.created_at)}
                    </span>
                  </div>
                  <h3 className="title">
                    <Link to={`/posts/${post.slug}`}>{post.title}</Link>
                  </h3>
                  <p className="excerpt">{post.excerpt || 'Tap through for the full briefing.'}</p>
                  <div className="archive-card-meta">
                    <span className="byline">{post.author?.name || 'Questflow Desk'}</span>
                    <div className="archive-tag-row">
                      {post.tags && post.tags.length > 0 ? (
                        post.tags.map((tag) => {
                          const slug = slugifyTag(tag);
                          const href = slug ? `/tag/${slug}` : '/archive';
                          return (
                            <Link
                              key={`${post.slug}-${tag}`}
                              to={href}
                              className="tag-pill ghost"
                              aria-label={`View posts tagged ${tag}`}
                            >
                              {tag}
                            </Link>
                          );
                        })
                      ) : (
                        <span className="muted">No tags yet</span>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      ) : (
        <div className="archive-table-wrapper">
          <table className="archive-table">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Author</th>
                <th scope="col">Date</th>
                <th scope="col">Tags</th>
                <th scope="col">Category</th>
              </tr>
            </thead>
            <tbody>
              {pagedPosts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="archive-empty">
                    {posts.length === 0
                      ? 'Archive is empty—publish your first story to fill it.'
                      : 'No posts match this search yet.'}
                  </td>
                </tr>
              ) : (
                pagedPosts.map((post) => (
                  <tr key={post.slug}>
                    <td data-label="Title">
                      <Link to={`/posts/${post.slug}`} className="archive-title-link">
                        {post.title}
                      </Link>
                    </td>
                    <td data-label="Author">{post.author?.name || 'Questflow Desk'}</td>
                    <td data-label="Date">{formatFullDate(post.date_published || post.created_at)}</td>
                    <td data-label="Tags">
                      {post.tags && post.tags.length > 0 ? (
                        <div className="archive-tag-row">
                          {post.tags.map((tag) => {
                            const slug = slugifyTag(tag);
                            const href = slug ? `/tag/${slug}` : '/archive';
                            return (
                              <Link
                                key={`${post.slug}-${tag}`}
                                to={href}
                                className="tag-pill ghost"
                                aria-label={`View posts tagged ${tag}`}
                              >
                                {tag}
                              </Link>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td data-label="Category">{post.category || 'General'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="archive-pagination">
        <button type="button" onClick={() => setPage((prev) => Math.max(prev - 1, 0))} disabled={page === 0}>
          Previous
        </button>
        <span>
          Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
          disabled={page >= totalPages - 1}
        >
          Next
        </button>
      </div>
    </section>
  );
}
